/**
 * Утилиты для синхронизации JWT токенов между main и worker клиентами
 * Решает race condition при авторизации
 * ВАЖНО: НЕ импортируем supabase клиент для избежания Multiple GoTrueClient instances
 */

/**
 * Ожидает готовности валидного токена в localStorage
 * @param maxRetries - максимальное количество попыток
 * @param delay - задержка между попытками в мс
 * @returns Promise<string> - JWT токен
 */
export async function waitForWorkerAuthReady(maxRetries: number = 10, delay: number = 200): Promise<string> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://aoucmkkqfbqccmcerwtr.supabase.co';
  const projectRef = extractProjectRef(supabaseUrl);
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      // Проверяем токен только в localStorage (без обращения к supabase клиенту)
      if (projectRef) {
        // Prefer the official Supabase key for reading, but do not write to it.
        const officialKey = `sb-${projectRef}-auth-token`;
        const workerMirrorKey = `ap-worker-auth-token-${projectRef}`;
        const raw = localStorage.getItem(officialKey) || localStorage.getItem(workerMirrorKey);
        
        if (raw) {
          const parsed = JSON.parse(raw) as Record<string, unknown>;
          const token = 
            (parsed?.currentSession as Record<string, unknown>)?.access_token as string | undefined ||
            (parsed?.access_token as string | undefined);
            
          if (token) {
            console.log(`[AuthTokenSync] Valid token found in localStorage on attempt ${i + 1}`);
            return token;
          }
        }
      }
      
      console.log(`[AuthTokenSync] No valid token in localStorage on attempt ${i + 1}, retrying...`);
      
      if (i < maxRetries - 1) {
        await new Promise(r => setTimeout(r, delay));
      }
    } catch (error) {
      console.error(`[AuthTokenSync] Error on attempt ${i + 1}:`, error);
      if (i < maxRetries - 1) {
        await new Promise(r => setTimeout(r, delay));
      } else {
        throw error;
      }
    }
  }
  
  throw new Error(`Worker client did not receive auth session in time after ${maxRetries} attempts`);
}

// Извлекаем project ref для правильного ключа localStorage
const extractProjectRef = (url: string): string | null => {
  try {
    const { hostname } = new URL(url);
    const [ref] = hostname.split('.');
    return ref || null;
  } catch {
    return null;
  }
};

/**
 * Принудительно синхронизирует токен из AuthContext в localStorage
 * для использования worker клиентом (НЕ обращается к supabase клиенту)
 */
export async function syncTokenToWorker(session?: { access_token?: string; user?: unknown } | null): Promise<void> {
  try {
    if (!session?.access_token) {
      console.warn('[AuthTokenSync] No session provided for sync');
      throw new Error('No valid session to sync');
    }
    
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://aoucmkkqfbqccmcerwtr.supabase.co';
    const projectRef = extractProjectRef(supabaseUrl);
    
    if (projectRef) {
      // Write only to our dedicated worker mirror key to avoid interfering with Supabase internals
      const workerMirrorKey = `ap-worker-auth-token-${projectRef}`;
      const sessionData = {
        currentSession: session,
        access_token: session.access_token,
        user: session.user
      };
      
      localStorage.setItem(workerMirrorKey, JSON.stringify(sessionData));
      
      console.log('[AuthTokenSync] Token successfully synced to worker storage');
    } else {
      console.warn('[AuthTokenSync] Could not extract project ref for storage key');
    }
    
  } catch (error) {
    console.error('[AuthTokenSync] Failed to sync token:', error);
    throw error;
  }
}

/**
 * Комбинированная функция: синхронизирует токен и ждет его готовности
 * Рекомендуется использовать эту функцию перед worker операциями
 */
export async function ensureWorkerAuthReady(session?: { access_token?: string; user?: unknown } | null): Promise<string> {
  try {
    // Проверяем, есть ли уже валидный токен в localStorage
    const hasToken = await hasValidMainToken();
    if (!hasToken && !session?.access_token) {
      console.warn('[AuthTokenSync] No valid token available, skipping sync');
      throw new Error('No valid token available for sync');
    }
    
    // Принудительно синхронизируем токен (если передан)
    if (session?.access_token) {
      await syncTokenToWorker(session);
    }
    
    // Затем ждем его готовности
    const token = await waitForWorkerAuthReady(5, 100);
    
    console.log('[AuthTokenSync] Worker auth is ready');
    return token;
    
  } catch (error) {
    console.error('[AuthTokenSync] Failed to ensure worker auth ready:', error);
    throw error;
  }
}

/**
 * Проверяет наличие валидного токена в localStorage
 */
/**
 * Получает userId из localStorage токена
 * Использует динамический PROJECT_REF для правильного ключа
 */
export function getCurrentUserIdFromStorage(): string | null {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://api.arendapro.plus';
    const projectRef = extractProjectRef(supabaseUrl);
    
    if (!projectRef) return null;
    
    const officialKey = `sb-${projectRef}-auth-token`;
    const workerMirrorKey = `ap-worker-auth-token-${projectRef}`;
    const raw = localStorage.getItem(officialKey) || localStorage.getItem(workerMirrorKey);
    
    if (!raw) return null;
    
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const userId = 
      (parsed?.user as Record<string, unknown>)?.id as string | undefined ||
      (parsed?.currentSession as Record<string, unknown> | undefined)?.user as Record<string, unknown> | undefined &&
      ((parsed?.currentSession as Record<string, unknown>)?.user as Record<string, unknown>)?.id as string | undefined;
      
    return userId || null;
  } catch (error) {
    console.error('[AuthTokenSync] Failed to get userId from storage:', error);
    return null;
  }
}

async function hasValidMainToken(): Promise<boolean> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://aoucmkkqfbqccmcerwtr.supabase.co';
    const projectRef = extractProjectRef(supabaseUrl);
    
    if (!projectRef) return false;
    
    const officialKey = `sb-${projectRef}-auth-token`;
    const workerMirrorKey = `ap-worker-auth-token-${projectRef}`;
    const raw = localStorage.getItem(officialKey) || localStorage.getItem(workerMirrorKey);
    
    if (!raw) return false;
    
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const token = 
      (parsed?.currentSession as Record<string, unknown>)?.access_token as string | undefined ||
      (parsed?.access_token as string | undefined);
      
    return !!token;
  } catch (error) {
    console.error('[AuthTokenSync] Failed to check main token:', error);
    return false;
  }
}
